import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Radio,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { commonGridStyles } from 'styles/gridStyles';
import { userService } from 'components/api/users';
import { referralService } from 'components/api/referral';

interface AddReferralModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (selectedUser: any, selectedCode: any) => Promise<void> | void;
  currentUserId: string;
}

const AddReferralModal: React.FC<AddReferralModalProps> = ({ open, onClose, onAdd, currentUserId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const [codes, setCodes] = useState<any[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [selectedCode, setSelectedCode] = useState<any | null>(null);

  useEffect(() => {
    if (debouncedSearch) {
      searchUsers(debouncedSearch);
    } else {
      setUsers([]);
    }
  }, [debouncedSearch]);

  const searchUsers = async (username: string) => {
    setLoadingUsers(true);
    try {
      const res = await userService.getUsers({
        page: 1,
        limit: 10,
        sortField: 'username',
        sortValue: 'ASC',
        username,
      } as any);
      const docs = (res as any).docs || (res as any).data?.docs || (res as any).data || [];
      setUsers(docs);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchReferralCodes = async (userId: string | number) => {
    setLoadingCodes(true);
    setCodes([]);
    try {
      const res = await referralService.getUserReferrals(userId.toString(), {
        page: 1,
        limit: 100,
      } as any);
      const campaigns = (res as any).campaigns || (res as any).data?.campaigns || [];
      setCodes(campaigns);
    } catch (error) {
      console.error('Error fetching referral codes:', error);
    } finally {
      setLoadingCodes(false);
    }
  };

  const handleSelectUser = (user: any) => {
    setSelectedUser(user);
    // Code selection is separate and we already fetch codes for current user
  };

  const handleAdd = async () => {
    if (selectedUser && selectedCode) {
      setIsSubmitting(true);
      try {
        await onAdd(selectedUser, selectedCode);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetState = () => {
    setSearchTerm('');
    setUsers([]);
    setSelectedUser(null);
    setCodes([]);
    setSelectedCode(null);
  };

  useEffect(() => {
    if (open) {
      fetchReferralCodes(currentUserId);
    } else {
      resetState();
    }
  }, [open, currentUserId]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90, renderCell: (params: any) => params.row.id || params.row.userId },
    { field: 'username', headerName: 'Username', width: 200 },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleSelectUser(params.row)}
          sx={{ textTransform: 'none', bgcolor: 'var(--accent-blue)', '&:hover': { bgcolor: 'var(--accent-blue)', filter: 'brightness(0.85)' } }}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'var(--bg-panel)', color: 'var(--text-primary)' }}>
        Add Referral
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'var(--bg-panel-secondary)', color: 'var(--text-primary)', minHeight: '400px' }}>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* User Search Step */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              1. Search User
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search user by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                input: { color: 'var(--text-primary)' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'var(--text-muted)' },
                  '&:hover fieldset': { borderColor: 'var(--text-primary)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--accent-blue)' },
                },
              }}
            />
          </Box>

          {!selectedUser && debouncedSearch && (
            <Box sx={{ height: 300, width: '100%' }}>
              <DataGrid
                rows={users}
                getRowId={(row) => row.id || row.userId || Math.random()}
                columns={columns}
                loading={loadingUsers}
                hideFooter
                sx={{
                  ...commonGridStyles,
                  border: '1px solid var(--border-color)',
                  '& .MuiDataGrid-cell': { borderColor: 'var(--border-color)' },
                }}
              />
            </Box>
          )}

          {/* Selected User Info */}
          {selectedUser && (
            <Box sx={{ p: 2, border: '1px solid var(--accent-blue)', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>
                Selected User: <strong>{selectedUser.username}</strong>
              </Typography>
              <Button size="small" onClick={() => setSelectedUser(null)} sx={{ color: 'var(--text-secondary)' }}>
                Change User
              </Button>
            </Box>
          )}

          {/* Code Selection Step */}
          {selectedUser && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                2. Select Referral Code
              </Typography>
              {loadingCodes ? (
                <CircularProgress size={24} />
              ) : codes.length > 0 ? (
                  <List sx={{ border: '1px solid var(--border-color)', borderRadius: 1, p: 0 }}>
                    {codes.map((codeItem) => (
                      <ListItem key={codeItem.id} disablePadding>
                        <ListItemButton onClick={() => setSelectedCode(codeItem)} sx={{ p: '4px' }}>
                          <Radio
                            checked={selectedCode?.id === codeItem.id}
                            sx={{ color: 'var(--text-secondary)', '&.Mui-checked': { color: 'var(--accent-blue)' } }}
                          />
                          <ListItemText 
                            primary={`Campaign: ${codeItem.name || 'Unknown'}`} 
                            secondary={<Typography variant="body2" sx={{color: 'var(--text-muted)'}}>Code: {codeItem.code}</Typography>} 
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography sx={{ color: 'var(--text-secondary)' }}>No referral codes found for this user.</Typography>
                )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'var(--bg-panel)', p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'var(--text-secondary)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!selectedUser || !selectedCode || isSubmitting}
          sx={{
            bgcolor: '#2a8039',
            '&:hover': { bgcolor: '#1e662a' },
            '&.Mui-disabled': { bgcolor: 'var(--bg-panel)', color: 'var(--text-muted)' }
          }}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferralModal;
